import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useAppDispatch } from "app/hooks";
import {
  Button,
  Field,
  FieldsContainer,
  globalMessageHandler,
  PasswordField,
  Spacer,
} from "common";
import authService from "features/auth/authApi";
import { logoutAsync } from "features/identity/identitySlice";
import I18n from "i18n";
import { useState } from "react";

export interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mandatory?: boolean; // Ha true, akkor nem lehet bezárni
}

export default function ChangePasswordDialog({
  open,
  onClose,
  onSuccess,
  mandatory = false,
}: ChangePasswordDialogProps) {
  const dispatch = useAppDispatch();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setCurrentPassword("");
    setPassword("");
    setPasswordConfirmation("");
    setErrors({});
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const handleLogout = () => {
    dispatch(logoutAsync());
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.current_password = I18n.t(
        "Auth.changePassword.errors.currentPasswordRequired",
      );
    }
    if (!password) {
      newErrors.password = I18n.t(
        "Auth.changePassword.errors.passwordRequired",
      );
    }
    if (password && password.length < 8) {
      newErrors.password = I18n.t(
        "Auth.changePassword.errors.passwordMinLength",
      );
    }
    if (!passwordConfirmation) {
      newErrors.password_confirmation = I18n.t(
        "Auth.changePassword.errors.passwordConfirmationRequired",
      );
    }
    if (password !== passwordConfirmation) {
      newErrors.password_confirmation = I18n.t(
        "Auth.changePassword.errors.passwordsDoNotMatch",
      );
    }
    if (currentPassword === password) {
      newErrors.password = I18n.t(
        "Auth.changePassword.errors.passwordMustBeDifferent",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    const {
      success,
      data,
      error: apiError,
    } = await authService.changePassword({
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    });
    setLoading(false);
    console.log({ success, data, apiError });
    if (success) {
      globalMessageHandler.snack({
        message: data?.message || I18n.t("Auth.changePassword.success"),
        severity: "success",
      });
      onSuccess?.(); // Call success callback if provided
      handleClose();
    } else {
      // Handle validation errors from backend
      // apiError structure: { fieldName: [validationKey, ...errorMessages] }
      if (apiError && typeof apiError === "object") {
        const fieldErrors: Record<string, string> = {};
        let hasFieldErrors = false;

        for (const [field, messages] of Object.entries(apiError)) {
          if (Array.isArray(messages) && messages.length > 0) {
            hasFieldErrors = true;
            fieldErrors[field] = messages.join(" ");
          }
        }

        if (hasFieldErrors) {
          setErrors(fieldErrors);
          // Show first error message in snackbar
          const firstError = Object.values(fieldErrors)[0];
          globalMessageHandler.snack({
            message: firstError,
            severity: "error",
          });
        } else {
          const errorMessage = I18n.t(
            "Auth.changePassword.errors.failedToChange",
          );
          setErrors({ general: errorMessage });
          globalMessageHandler.snack({
            message: errorMessage,
            severity: "error",
          });
        }
      } else {
        const errorMessage = I18n.t(
          "Auth.changePassword.errors.failedToChange",
        );
        setErrors({ general: errorMessage });
        globalMessageHandler.snack({
          message: errorMessage,
          severity: "error",
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={mandatory ? undefined : handleClose}
      disableEscapeKeyDown={mandatory}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LockIcon />
          {I18n.t("Auth.changePassword.title")}
        </div>
        {!mandatory && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <FieldsContainer sx={{ mt: -1 }}>
          <Field fullWidth>
            <PasswordField
              label={I18n.t("Auth.changePassword.currentPasswordLabel")}
              value={currentPassword}
              onChange={(e: any) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              autoFocus
              disabled={loading}
              error={errors.current_password}
              helperText={errors.current_password}
            />
          </Field>
          <Field fullWidth>
            <PasswordField
              label={I18n.t("Auth.changePassword.newPasswordLabel")}
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={loading}
              error={errors.password}
              helperText={
                errors.password || I18n.t("Auth.changePassword.passwordHint")
              }
            />
          </Field>
          <Field fullWidth>
            <PasswordField
              label={I18n.t("Auth.changePassword.confirmPasswordLabel")}
              value={passwordConfirmation}
              onChange={(e: any) => setPasswordConfirmation(e.target.value)}
              required
              autoComplete="new-password"
              disabled={loading}
              error={errors.password_confirmation}
              helperText={errors.password_confirmation}
            />
          </Field>
        </FieldsContainer>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {mandatory && (
          <>
            <Button
              onClick={handleLogout}
              variant="outlined"
              disabled={loading}
              startIcon={<LogoutIcon />}
              sx={{ mr: "auto" }}
            >
              {I18n.t("App.logout")}
            </Button>
            <Spacer right />
          </>
        )}
        {!mandatory && (
          <Button onClick={handleClose} variant="outlined" disabled={loading}>
            {I18n.t("Auth.changePassword.cancelButton")}
          </Button>
        )}
        <Button
          type="submit"
          loading={loading}
          disabled={
            loading || !currentPassword || !password || !passwordConfirmation
          }
        >
          {I18n.t("Auth.changePassword.changeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
